import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabaseStorage';
import { useAppStore } from '../store/useAppStore';
import { AuthInlineWidget } from './AuthInlineWidget';
import { PublicAgentChat } from './public/PublicAgentChat';
import { LoadingSpinner } from './LoadingSpinner';
import type { AgentPricing, AgentTemplate } from '../types/agent';

export type AgentPreviewRow = {
  id: string;
  name: string;
  public_title: string | null;
  public_description: string | null;
  cover_media_url: string | null;
  cover_media_type: 'image' | 'video' | null;
  pricing: AgentPricing | null;
};

type AgentPreviewEndDrawerProps = {
  agent: AgentPreviewRow | null;
  open: boolean;
  onClose: () => void;
  /** Sign-in / sign-up only (e.g. header “Get started”) — no agent to load. */
  signInOnly?: boolean;
  /** After successful login or sign-up when `signInOnly` (defaults to /library + close). */
  onAuthSuccess?: () => void;
};

/**
 * End drawer hosts the same agent experience as `/apps/agent/:id`: onboarding, instance, and chat.
 * With `signInOnly`, the panel is just `AuthInlineWidget` (e.g. from “Get started”).
 * Not a launcher — users set up and chat inside the panel (requires sign-in).
 */
export function AgentPreviewEndDrawer({
  agent,
  open,
  onClose,
  signInOnly = false,
  onAuthSuccess,
}: AgentPreviewEndDrawerProps) {
  const navigate = useNavigate();
  const session = useAppStore((s) => s.session);
  const [template, setTemplate] = useState<AgentTemplate | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const handleHeaderAuthSuccess = () => {
    onAuthSuccess?.();
    onClose();
    void navigate('/library', { replace: true });
  };

  useEffect(() => {
    if (!open || signInOnly || !agent) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      setTemplate(null);
      const { data, error } = await supabase.from('agent_templates').select('*').eq('id', agent.id).maybeSingle();
      if (cancelled) return;
      if (error || !data) {
        setLoadError(error?.message || 'Template not found');
        setTemplate(null);
      } else {
        setTemplate(data as AgentTemplate);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, signInOnly, agent?.id]);

  useEffect(() => {
    if (open && signInOnly && session) {
      onClose();
    }
  }, [open, signInOnly, session, onClose]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  if (!signInOnly && !agent) return null;
  if (signInOnly && session) {
    return null;
  }

  /** One auth UI for “Get started” and for opening an agent while logged out — only success handlers differ. */
  const signInFromHeader = signInOnly;

  const panelContent = !session ? (
    <div className="relative flex min-h-0 flex-1 flex-col overflow-y-auto">
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
        <div className="absolute -right-16 -top-10 h-64 w-64 rounded-full bg-brand/25 blur-3xl" />
        <div className="absolute -bottom-20 left-0 h-72 w-72 rounded-full bg-brand/10 blur-3xl" />
        <div className="absolute left-1/2 top-0 h-32 w-[min(100%,28rem)] -translate-x-1/2 bg-gradient-to-b from-brand/15 to-transparent" />
      </div>
      <div className="relative z-10 flex min-h-0 flex-1 flex-col">
        <div className="flex shrink-0 items-center justify-end gap-2 px-4 pt-[max(0.75rem,env(safe-area-inset-top))] pb-1 sm:px-6">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl p-2.5 text-th-secondary transition hover:bg-overlay hover:text-th-primary"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center px-4 pb-10 pt-1 sm:px-6 sm:pb-12 sm:pt-2">
          <AuthInlineWidget
            layout="panel"
            panelStyle="drawerPromo"
            defaultView="login"
            onLoginSuccess={signInFromHeader ? handleHeaderAuthSuccess : undefined}
            onSignupSuccess={signInFromHeader ? handleHeaderAuthSuccess : undefined}
          />
        </div>
      </div>
    </div>
  ) : loading ? (
    <div className="flex flex-1 items-center justify-center">
      <LoadingSpinner size="lg" />
    </div>
  ) : loadError ? (
    <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 text-center text-sm text-th-secondary">
      <p>{loadError}</p>
      <button type="button" className="text-brand hover:underline" onClick={onClose}>
        Close
      </button>
    </div>
  ) : template ? (
    <div className="min-h-0 flex-1 overflow-hidden">
      <PublicAgentChat
        pageConfig={{
          id: template.id,
          route_path: template.id,
          page_title: template.name,
          page_description: template.public_description || undefined,
          app_id: 'web',
        }}
        template={template}
        session={session}
        embedInPanel
        onClosePanel={onClose}
      />
    </div>
  ) : null;

  return (
    <div className="fixed inset-0 z-modal-content flex justify-end" role="presentation">
      <button
        type="button"
        className="absolute inset-0 z-0 bg-base/80 backdrop-blur-sm"
        aria-label="Close"
        onClick={onClose}
      />
      <aside
        className={
          !session
            ? 'relative z-10 flex h-full w-full max-w-full flex-col border-l border-subtle/70 bg-gradient-to-b from-elevated/30 via-base to-base shadow-2xl animate-slide-in-right md:w-[60%] lg:w-[40%] lg:max-w-[min(100%,28rem)]'
            : 'relative z-10 flex h-full w-full max-w-full flex-col border-l border-subtle bg-base shadow-2xl animate-slide-in-right md:w-[60%] lg:w-[40%]'
        }
        role="dialog"
        aria-modal="true"
        aria-label={!session ? 'Sign in' : 'Agent'}
      >
        <div className="min-h-0 flex-1 flex flex-col overflow-hidden">{panelContent}</div>
      </aside>
    </div>
  );
}
